/**
 * NotificationBell Component
 *
 * Bell icon with unread badge that toggles the notification dropdown.
 * Works on both desktop (dropdown) and mobile (opens panel).
 */

import { useState, useRef, useCallback } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { useClickOutside } from '../../hooks/useClickOutside';
import { NotificationDropdown } from './NotificationDropdown';
import { TradeModal } from '../TradeModal';
import type { TradeNotification } from '@drawn-of-war/shared';
import styles from './NotificationDropdown.module.css';

interface NotificationBellProps {
  /** When true, triggers mobile panel instead of dropdown */
  isMobile?: boolean;
  /** Callback to open mobile notifications panel */
  onMobileOpen?: () => void;
}

/**
 * Bell SVG icon
 */
function BellIcon({ hasUnread }: { hasUnread: boolean }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill={hasUnread ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={styles.bellSvg}
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

export function NotificationBell({ isMobile = false, onMobileOpen }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tradeModalTarget, setTradeModalTarget] = useState<{ userId: string; displayName: string } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    acceptInvite,
    declineInvite,
    acceptTrade,
    declineTrade
  } = useNotifications();

  // Handle viewing a trade - opens the TradeModal
  const handleViewTrade = useCallback((tradeId: string) => {
    // Find the notification with this trade
    const notification = notifications.find(n => n.id === `trade-${tradeId}`);
    if (notification && notification.type === 'trade') {
      const tradeData = notification.data as TradeNotification;
      setTradeModalTarget({
        userId: tradeData.withUser.id,
        displayName: tradeData.withUser.displayName
      });
      setIsOpen(false); // Close the dropdown
    }
  }, [notifications]);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsOpen(false), isOpen && !isMobile);

  // Handle keyboard escape
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
  }, [isOpen]);

  const handleClick = () => {
    if (isMobile && onMobileOpen) {
      onMobileOpen();
    } else {
      setIsOpen(!isOpen);
    }
  };

  // Format badge count
  const badgeText = unreadCount > 9 ? '9+' : unreadCount.toString();

  return (
    <div className={styles.bellWrapper} onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        className={styles.bellButton}
        onClick={handleClick}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <BellIcon hasUnread={unreadCount > 0} />
        {unreadCount > 0 && (
          <span className={styles.badge} aria-hidden="true">
            {badgeText}
          </span>
        )}
      </button>

      {isOpen && !isMobile && (
        <NotificationDropdown
          ref={dropdownRef}
          notifications={notifications}
          isLoading={isLoading}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onAcceptInvite={acceptInvite}
          onDeclineInvite={declineInvite}
          onAcceptTrade={acceptTrade}
          onDeclineTrade={declineTrade}
          onViewTrade={handleViewTrade}
        />
      )}

      {/* Trade Modal - opened when viewing a trade from notifications */}
      {tradeModalTarget && (
        <TradeModal
          isOpen={true}
          onClose={() => setTradeModalTarget(null)}
          targetUserId={tradeModalTarget.userId}
          targetDisplayName={tradeModalTarget.displayName}
        />
      )}
    </div>
  );
}
